import type { DefaultFallback, FlattenedCommand } from '../../types/commands/index.tsx';
import type { ResultUseMatches } from '../../types/use-matches/index.tsx';

// remove when cleaning jjj_ask_rovo_compal_v1
export const getFallbackCommandResultOld = (
	fallback: DefaultFallback | undefined,
): ResultUseMatches | undefined => {
	if (!fallback || !fallback?.onNoResults?.fallbackCommand) {
		return undefined;
	}
	const { fallbackCommand, fallbackSection } = fallback.onNoResults;

	const section = fallbackSection;

	const fallbackCommandHeader =
		fallbackCommand && section
			? {
					...(section.renderName
						? {
								renderGroup: section.renderName,
							}
						: {
								group: section.name,
							}),

					isGroup: true,
				}
			: undefined;

	return {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		results: [fallbackCommandHeader, { command: fallbackCommand, active: true, index: 0 }].filter(
			Boolean,
		) as FlattenedCommand[],
		total: 1,
		activeCommand: fallbackCommand,
		fallback,
	};
};

export const getFallbackCommandResult = (
	fallback: DefaultFallback | undefined,
	activeIndex: number = 0,
): ResultUseMatches | undefined => {
	if (
		!fallback ||
		(!fallback?.onNoResults?.fallbackCommand && !fallback?.onNoResults?.fallbackCommands)
	) {
		return undefined;
	}

	const { fallbackCommands, fallbackSection } = fallback.onNoResults;
	const section = fallbackSection;

	const commands = fallbackCommands || [];

	if (commands.length === 0) {
		return undefined;
	}

	// Create section header (e.g., "Recommended") - this is NOT actionable via keyboard
	// Headers appear in the UI but don't participate in keyboard navigation
	const fallbackCommandHeader =
		commands.length > 0 && section
			? {
					...(section.renderName
						? {
								renderGroup: section.renderName,
							}
						: {
								group: section.name,
							}),
					isGroup: true, // Marks this as a section header, not an executable command
				}
			: undefined;

	// Transform commands into navigable results with correct active/index calculations
	// Sequential counter for actionable commands only (excludes headers)
	let actionableIndex = 0;

	const commandResults = commands.map((command) => {
		const result = {
			command,

			// ACTIVE CALCULATION: This command is visually highlighted if its position matches keyboard position
			// Example: When activeIndex=1, only the command at actionableIndex=1 will have active=true
			// This drives the visual highlighting and determines which command executes on Enter
			active: activeIndex === actionableIndex,

			// INDEX ASSIGNMENT: Sequential position for keyboard navigation boundaries
			// Used by navigation logic to know valid range (0 to total-1)
			// Example: [Command1=0, Command2=1] allows ArrowDown from 0->1, ArrowUp from 1->0
			index: actionableIndex,
		};

		actionableIndex++;
		return result;
	});

	return {
		// Combine header + commands, filter out undefined header if no section configured
		// Final structure: [SectionHeader?, Command1, Command2, ...]
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		results: [fallbackCommandHeader, ...commandResults].filter(Boolean) as FlattenedCommand[],

		// TOTAL COUNT: Number of actionable commands (used for navigation boundaries)
		// Keyboard navigation uses this to prevent going beyond valid indices
		// Example: total=2 means valid activeIndex range is 0-1
		total: actionableIndex,

		// ACTIVE COMMAND: The command object that should execute when user presses Enter
		// Must match the command that has active=true in the results array
		// The || commands[0] is a defensive fallback - should never be needed due to activeIndex reset logic,
		// but prevents crashes if activeIndex somehow becomes out of bounds
		activeCommand: commands.find((_, index) => index === activeIndex) || commands[0],

		fallback,
	};
};
